import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import * as styles from './Sidebar.module.scss';
import SidebarRow from './SidebarRow';
import SidebarSections from './SidebarSections';
import SidebarSectionsModal, {
  SIDEBAR_SECTIONS_CLICK_EVENTS,
} from './SidebarSectionsModal';
import SidebarSectionsProvider from './SidebarSectionsProvider';
import TeamButton from './TeamButton';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Avatar from '../components/Avatar';
import SVGIcon from '../components/SVGIcon';
import HelpMenu from '../help/HelpMenu';
import subtopicsIcon from '../icons/non-prefilled/16x16/subtopics.svg';
import draftsIcon from '../icons/prefilled/16x16/drafts.svg';
import groupIcon from '../icons/prefilled/16x16/group.svg';
import homeIcon from '../icons/prefilled/16x16/home.svg';
import NotificationButton from '../notification/NotificationButton';
import { UIActions } from '../ui/uiSlice';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import userPath from '../utils/url/userPath';

const USER_AVATAR_SIZE = parseInt(styles.userAvatarSize, 10);

const usePreventBodyScrolling = () => {
  const isOpen = useIsSidebarOpen();
  const isMobile = useIsSize(Sizes.SM);

  const shouldPrevent = isOpen && isMobile;
  useEffect(() => {
    if (shouldPrevent) {
      document.body.classList.add('prevent-scrolling');
    } else {
      document.body.classList.remove('prevent-scrolling');
    }
  }, [shouldPrevent]);
};

const Sidebar = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  const currentUser = useAppSelector((state) => state.session.user);
  const isOpen = useIsSidebarOpen();

  const dispatch = useAppDispatch();
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const isGuest = currentUser.type === 'GUEST';

  usePreventBodyScrolling();

  return (
    <SidebarSectionsProvider>
      <PageLayout.Sidebar
        className={classNames(styles.sidebar, {
          [styles.scrolled]: isScrolled,
          [styles.isSidebarSectionsModalOpen]: selectedSectionId != null,
        })}
        aria-label="Sidebar"
      >
        <header className={styles.teamNavRow}>
          <div className={styles.teamNavRowInner}>
            <TeamButton />
            <NotificationButton />
          </div>
        </header>
        <div className={styles.navs}>
          <Waypoint
            onEnter={() => {
              setIsScrolled(false);
            }}
            onLeave={() => {
              setIsScrolled(true);
            }}
          />
          <div className={styles.mainNavs} aria-label="Site Navigation">
            <SidebarRow
              icon={
                <span className={styles.userAvatar}>
                  <Avatar size={USER_AVATAR_SIZE} user={currentUser} />
                </span>
              }
              to={userPath(currentUser)}
              onlyActiveOnIndex
            >
              {currentUser.name}
            </SidebarRow>

            <SidebarRow icon={homeIcon} to="/" onlyActiveOnIndex>
              Home
            </SidebarRow>

            <SidebarRow icon={draftsIcon} to="/drafts" onlyActiveOnIndex>
              Drafts
            </SidebarRow>

            {!isGuest && (
              <SidebarRow
                icon={
                  <SVGIcon className={classNames(styles.contentMapIcon)}>
                    {subtopicsIcon}
                  </SVGIcon>
                }
                to="/topics"
                onlyActiveOnIndex
              >
                Content Map
              </SidebarRow>
            )}

            <SidebarRow icon={groupIcon} to="/groups">
              Groups
            </SidebarRow>
          </div>
          <div
            onMouseDown={(e) => {
              SIDEBAR_SECTIONS_CLICK_EVENTS.add(e.nativeEvent);
            }}
          >
            <SidebarSections />
          </div>
        </div>
        <HelpMenu appearance="internal" />
      </PageLayout.Sidebar>
      <div
        className={classNames(styles.clickOutside, {
          [styles.show]: isOpen,
        })}
        onClick={() => dispatch(UIActions.toggleSidebar())}
      />
      <SidebarSectionsModal />
    </SidebarSectionsProvider>
  );
};

export default Sidebar;
